'use client'

import { useEffect } from 'react'
import { useRouter } from 'next/navigation'
import { AlertTriangle, RefreshCw, Home } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import * as Sentry from '@sentry/nextjs'

interface ErrorViewProps {
  error: Error & { digest?: string }
  reset: () => void
}

export function ErrorView({ error, reset }: ErrorViewProps) {
  const router = useRouter()

  useEffect(() => {
    Sentry.captureException(error)
  }, [error])

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-gray-50 flex items-center justify-center p-4">
      <Card className="max-w-md w-full border-0 shadow-xl">
        <CardContent className="pt-8 pb-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <AlertTriangle className="w-8 h-8 text-red-600" />
          </div>

          <h1 className="text-xl font-bold text-gray-900 mb-2">エラーが発生しました</h1>

          <p className="text-muted-foreground mb-6">
            申し訳ございません。予期しないエラーが発生しました。
            <br />
            再度お試しいただくか、ホームに戻ってください。
          </p>

          {/* 本番でもエラー要約を1行だけ表示 (原因特定とユーザーサポートのため)。
              詳細スタックは開発環境のみ。 */}
          {error.message && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 text-left">
              <p className="text-xs font-mono text-red-800 break-all">{error.message}</p>
              {process.env.NODE_ENV !== 'production' && error.stack && (
                <pre className="text-xs text-red-700 mt-2 whitespace-pre-wrap overflow-x-auto max-h-48 overflow-y-auto">
                  {error.stack}
                </pre>
              )}
            </div>
          )}
          {error.digest && (
            <p className="text-xs text-subtle mb-6 font-mono">Error ID: {error.digest}</p>
          )}

          <div className="flex gap-3 justify-center">
            <Button variant="outline" onClick={() => router.push('/bt-progress')}>
              <Home className="w-4 h-4 mr-2" />
              ホームへ
            </Button>
            <Button onClick={reset}>
              <RefreshCw className="w-4 h-4 mr-2" />
              再試行
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
